import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import { ImageCaption } from '../../Image/ImageCaption'
import { ImageCredit } from '../../Image/ImageCredit'
import { Picture } from '../../Picture'
import styles from './HeroImage.module.scss'

export const HeroImage: FunctionComponent<Props> = ({
  alt = '',
  caption,
  className,
  credit,
  cropDirection,
  sources,
  src,
  lazyLoad,
  ...rest
}) => {
  return (
    <figure
      className={classnames(styles.heroImageWrapper, className)}
      data-testid="hero-image-outer-wrapper"
      {...rest}
    >
      <div className={styles.imageCaptionWrapper}>
        <Picture
          alt={alt}
          aspectRatioMain="three-to-one"
          aspectRatioSm="two-to-one"
          className={classnames(styles.heroImage)}
          cropFrom={cropDirection}
          src={src}
          sources={sources}
          lazyLoad={lazyLoad}
        />

        {credit && (
          <ImageCredit
            className={classnames(
              styles.imageElementCreditImage,
              caption
                ? styles.imageElementCreditWithCaption
                : styles.imageElementCredit
            )}
            {...{ credit }}
          />
        )}
      </div>

      {caption && (
        <ImageCaption
          className={classnames(
            'sb-margin-top-1',
            'sb-margin-bottom-2',
            styles.imageElementCaption
          )}
          {...{ caption }}
        />
      )}
    </figure>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Optional string to determine whether to crop the hero image from the left or right on mobile view.
   */
  cropDirection?: 'left' | 'right'
  /**
   * Alt text to be displayed for the image
   */
  alt?: string
  /**
   * Credit to external photographers if required, appears in bottom right corner of image
   */
  credit?: string
  /**
   * Optional caption to be displayed under the hero image
   */
  caption?: string
  /**
   * Class name that can be applied to the hero image outer wrapper for extra styling purposes
   */
  className?: string
  /**
   * Source link to hero image to be displayed. NB This image must be in jpeg or gif format.
   */
  src: string
  /**
   * Array of image sources that can be rendered for responsive breakpoints.
   */
  sources?: Array<{
    srcset: string
    media?: string
  }>
  /**
   * Boolean to control if image should be lazy loaded
   */
  lazyLoad?: boolean
} & ComponentPropsWithoutRef<'figure'>
